import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { Monto } from '@/ui/Numero'
import { useOrdenes } from '@/dominio/compras/useCompras'
import { NOMBRE_ESTADO_ORDEN, type OrdenDetalle } from '@/dominio/compras/tipos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'
import { ErrorCompras, TD, TH } from './-compras/comun'
import { DetalleOrden, PanelOrden, TONO_ORDEN } from './-compras/Ordenes'

/**
 * Órdenes de compra (`docs/adr/0028` §3), del plan base. La orden abierta va en
 * la URL para pasar el enlace a quien la aprueba.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute('/compras')({
  validateSearch: (b: Record<string, unknown>): { orden?: string } => ({
    orden: typeof b.orden === 'string' && UUID.test(b.orden) ? b.orden : undefined,
  }),
  component: PantallaCompras,
})

function PantallaCompras() {
  const navegar = useNavigate({ from: Route.fullPath })
  const { orden: abierta } = Route.useSearch()
  const [estado, setEstado] = useState('')
  const [texto, setTexto] = useState('')
  const ordenes = useOrdenes({ estado: estado || undefined, texto: texto.trim() || undefined })
  const [formulario, setFormulario] = useState<OrdenDetalle | 'nueva' | null>(null)
  const abrir = (id: string | null) => void navegar({ search: { orden: id ?? undefined }, replace: true })

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Órdenes de compra"
        descripcion="Se arma en borrador, la aprueba quien autoriza y se le manda al proveedor por correo o por WhatsApp. Se recibe en partes y se cierra con su factura."
        acciones={
          <Boton variante="primario" icono={<Plus />} onClick={() => setFormulario('nueva')}>
            Nueva orden
          </Boton>
        }
      />

      <Tarjeta relleno={false}>
        <div className="grid gap-3 p-3 sm:grid-cols-3">
          <CampoFormulario etiqueta="Buscar">
            {(a) => <EntradaTexto {...a} placeholder="Número, proveedor o RNC" value={texto} onChange={(e) => setTexto(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Estado">
            {(a) => (
              <Selector {...a} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="">Todas</option>
                {Object.entries(NOMBRE_ESTADO_ORDEN).map(([valor, nombre]) => (
                  <option key={valor} value={valor}>
                    {nombre}
                  </option>
                ))}
              </Selector>
            )}
          </CampoFormulario>
        </div>
        <ErrorCompras error={ordenes.error} />
        {!ordenes.data ? (
          <div className="p-3">
            <FilasEsqueleto filas={5} columnas={5} />
          </div>
        ) : ordenes.data.length === 0 ? (
          <EstadoVacio
            variante={texto || estado ? 'sin-resultados' : 'primera-vez'}
            mensaje={texto || estado ? 'Ninguna orden coincide.' : 'Sin órdenes todavía. Arme la primera: el proveedor la recibe en PDF.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-cuerpo">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300">
                  <th className={TH}>Número</th>
                  <th className={TH}>Fecha</th>
                  <th className={TH}>Proveedor</th>
                  <th className={TH}>Entrega</th>
                  <th className={`${TH} text-right`}>Total</th>
                  <th className={TH}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {ordenes.data.map((o) => (
                  <tr
                    key={o.id}
                    className="h-[var(--alto-fila)] cursor-pointer border-b border-gris-200 last:border-b-0 hover:bg-gris-50"
                    onClick={() => abrir(o.id)}
                  >
                    <td className={`${TD} numero`}>{o.numeroTexto}</td>
                    <td className={`${TD} numero`}>{formatearFecha(o.fecha)}</td>
                    <td className={TD}>{o.proveedorNombre}</td>
                    <td className={`${TD} numero`}>{o.entregaEsperada ? formatearFecha(o.entregaEsperada) : '—'}</td>
                    <td className={`${TD} text-right`}>
                      <Monto valor={o.total} />
                    </td>
                    <td className={TD}>
                      <InsigniaEstado tono={TONO_ORDEN[o.estado]}>{o.estadoNombre}</InsigniaEstado>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      {abierta ? (
        <DetalleOrden
          id={abierta}
          onCerrar={() => abrir(null)}
          onEditar={(o) => {
            abrir(null)
            setFormulario(o)
          }}
        />
      ) : null}
      {formulario ? (
        <PanelOrden
          key={formulario === 'nueva' ? 'nueva' : formulario.orden.id}
          orden={formulario === 'nueva' ? null : formulario}
          onCerrar={() => setFormulario(null)}
          onCreada={(id) => abrir(id)}
        />
      ) : null}
    </div>
  )
}
